// hooks/useTokenInfo.js
import { useState, useEffect } from "react";
import { useChainId, useAccount } from "wagmi";
import ERC20_ABI from "../utils/abis/erc20TokenABI.json";
import { publicClients } from "../lib/wagmi";
import { formatUnits } from "viem";

export const useTokenInfo = (tokenAddress, userAddress, contractAddress) => {
    const [tokenInfo, setTokenInfo] = useState({
        balance: null,              // formatted user balance
        balanceWei: null,           // raw user balance (BigInt)
        contractBalance: null,      // formatted contract balance
        contractBalanceWei: null,   // raw contract balance (BigInt)
        symbol: null,
        decimals: null,
        isLoading: false,
        error: null,
    });

    const chainId = useChainId();
    const { address: connectedAddress } = useAccount();
    const finalAddress = userAddress || connectedAddress;

    useEffect(() => {
        const fetchTokenInfo = async () => {
            if (!chainId || !tokenAddress || !finalAddress) return;

            setTokenInfo(prev => ({ ...prev, isLoading: true, error: null }));

            try {
                // Prepare all contract calls
                const calls = [
                    publicClients[chainId].readContract({
                        address: tokenAddress,
                        abi: ERC20_ABI,
                        functionName: "balanceOf",
                        args: [finalAddress],
                    }),
                    publicClients[chainId].readContract({
                        address: tokenAddress,
                        abi: ERC20_ABI,
                        functionName: "symbol",
                    }),
                    publicClients[chainId].readContract({
                        address: tokenAddress,
                        abi: ERC20_ABI,
                        functionName: "decimals",
                    }),
                ];

                // Add contract balance call if contractAddress is provided
                if (contractAddress) {
                    calls.push(
                        publicClients[chainId].readContract({
                            address: tokenAddress,
                            abi: ERC20_ABI,
                            functionName: "balanceOf",
                            args: [contractAddress],
                        })
                    );
                }

                const results = await Promise.all(calls);
                const [balanceWei, symbol, decimals, contractBalanceWei] = results;

                const balance = Number(formatUnits(balanceWei, decimals));
                const contractBalance = contractBalanceWei
                    ? Number(formatUnits(contractBalanceWei, decimals))
                    : null;

                setTokenInfo({
                    balance,
                    balanceWei,
                    contractBalance,
                    contractBalanceWei,
                    symbol,
                    decimals,
                    isLoading: false,
                    error: null,
                });
            } catch (error) {
                console.error("Error fetching token info:", error);
                setTokenInfo({
                    balance: null,
                    balanceWei: null,
                    contractBalance: null,
                    contractBalanceWei: null,
                    symbol: null,
                    decimals: null,
                    isLoading: false,
                    error,
                });
            }
        };

        fetchTokenInfo();
    }, [chainId, tokenAddress, finalAddress, contractAddress]);

    return tokenInfo;
};